import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Grid,
} from '@mui/material';
import {
  DescriptionOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { waktuCabang } from '../../../utils/waktuCabang';
import { apiHost } from '../../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

const OrderDetailRetail = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [summary, setSummary] = useState({});

  useEffect(() => {
    fetchOrders();
  }, [selectedDate]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${getApiBaseUrl()}/rekapharianuser`, {
        params: { tanggal: selectedDate },
        withCredentials: true,
      });
      const data = res.data?.data || {};
      const allOrders = [
        ...(data.transaksiSuccess || []),
        ...(data.transaksiPending || []),
      ];
      setOrders(allOrders);
      setSummary({
        totalSuccess: res.data?.totalPenjualanSuccess || 0,
        totalPending: res.data?.totalPenjualanPending || 0,
        totalCash: res.data?.totalPenjualanCashSuccess || 0,
        totalQris: res.data?.totalPenjualanQrisSuccess || 0,
      });
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'selesai' || status === 'settlement') return 'success';
    if (status === 'pending') return 'warning';
    if (status === 'cancel' || status === 'cancelled' || status === 'expire') return 'error';
    return 'default';
  };

  const bs3 = {
    panelDefault: {
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: 'none',
      mb: 2,
      bgcolor: '#fff',
    },
    tableStriped: {
      '& .MuiTableRow-root:nth-of-type(odd)': { bgcolor: '#f9f9f9' },
      '& .MuiTableRow-root:hover': { bgcolor: '#f5f5f5' },
      '& .MuiTableCell-root': {
        fontSize: '13px',
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        py: 1,
        px: 1,
        borderBottom: '1px solid #ddd',
      },
      '& .MuiTableCell-head': {
        fontWeight: 700,
        bgcolor: '#f5f5f5',
        borderBottom: '2px solid #ddd',
        fontSize: '12px',
        textTransform: 'uppercase',
        color: '#555',
      },
    },
  };

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 } }}>
      {/* Date Filter */}
      <Card variant="outlined" sx={{ ...bs3.panelDefault, mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                width: { xs: '100%', sm: '200px' },
                '& .MuiOutlinedInput-root': {
                  fontSize: '13px',
                  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#666' }}>
                  Success
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#28a745' }}>
                  Rp {Number(summary.totalSuccess || 0).toLocaleString('id-ID')}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#666' }}>
                  Pending
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#ffc107' }}>
                  Rp {Number(summary.totalPending || 0).toLocaleString('id-ID')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ ...bs3.panelDefault }}>
        <CardHeader
          title="Detail Order"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
          subheader={loading ? 'Memuat order...' : `${orders.length} order`}
          subheaderTypographyProps={{ variant: 'caption' }}
          action={
            <Tooltip title="Refresh">
              <IconButton onClick={fetchOrders} disabled={loading}>
                <RefreshOutlined />
              </IconButton>
            </Tooltip>
          }
          sx={{ pb: 1 }}
        />

        <CardContent sx={{ pt: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : orders.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
              <DescriptionOutlined sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
              <Typography>Tidak ada order</Typography>
            </Box>
          ) : (
            <Table size="small" sx={bs3.tableStriped}>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Pelanggan</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tanggal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.uuid || order.id}>
                    <TableCell>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {order.order_id || order.order_id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{order.namaCustomer || '-'}</TableCell>
                    <TableCell>
                      Rp {Number(order.totaljual || 0).toLocaleString('id-ID')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status_pembayaran || order.status || 'pending'}
                        size="small"
                        color={getStatusColor(order.status_pembayaran || order.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {waktuCabang(order.createdAt || order.created_at).toLocaleDateString('id-ID')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrderDetailRetail;